import React, { useState } from 'react';
import { Button } from 'rsuite';

import { usersService, authService } from '../../../../services';
import { ROLES, ROLE_STRING_TO_ID } from '../../../../constants/permissions/roles';
import { palette } from '../../../../styles/theme';
import { EditUserSVG, EmailSVG, InfoSVG } from '../../../../components';
import { Box, CircularProgress, Dialog, Divider, FormHelperText, Stack, TextField, Typography, MenuItem, Select, FormControl } from '@mui/material';
import { useTranslation } from 'react-i18next';

const INITIAL_ERROR_STATE = { dni: '', fullname: '', email: '', role: '' };
const EMAIL_IN_USE_ERROR = 'Bad request. The email is already in use by another user.';

export function EditUserDialog ({ show, onHide, user, setAlert, setOpenAlert, refreshTable }) {
    const { t } = useTranslation();

    const roleLabels = {
        [ROLES.admin]: t('users.table.status.admin.natura'),
        [ROLES.operator]: t('users.table.status.operator.natura'),
        [ROLES.userAdmin]: t('users.table.status.user_admin.natura'),
        [ROLES.externalSupport]: t('users.table.status.ext-support-operator.natura'),
        [ROLES.fraudPrevention]: t('users.table.status.fraud_prevention_analyst.natura'),
        [ROLES.fraudPreventionAdmin]: t('users.table.status.fraud_prevention_admin.natura'),
    };

    // States

    // Form values states
    const fullname = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    // When the user has no active backoffice email, `normalize` sets it to '-'. Start the input
    // empty in that case so the operator types a real address instead of editing a placeholder.
    const initialEmail = user.email && user.email !== '-' ? user.email : '';
    const [email, setEmail] = useState(initialEmail);
    const initialRole = ROLE_STRING_TO_ID[user.role] || '';
    const [role, setRole] = useState(initialRole);
    const [error, setError] = useState(INITIAL_ERROR_STATE);
    const [isLoading, setIsLoading] = useState(false);

    const document = user.document;
    const documentType = user.document_type || user.documentType || 'INE';

    const hasEmailChanged = email !== initialEmail;
    const hasRoleChanged = role !== initialRole;
    const hasChanges = hasEmailChanged || hasRoleChanged;

    // Functions
    const editUser = () => {
        // Validate all fields
        if (!validate()) {
            return false;
        }

        setError(INITIAL_ERROR_STATE);
        setIsLoading(true);

        // Build decoupled register data promises
        const promises = [];

        if (hasEmailChanged) {
            const emailData = {
                email: email,
                idUser: user.id
            };
            promises.push(authService.verifyBoUserEmail(emailData));
        }

        if (hasRoleChanged) {
            const oldRoleId = ROLE_STRING_TO_ID[user.role];
            const roleData = {
                toDelete: oldRoleId,
                toCreate: { idRole: role }
            };
            promises.push(usersService.editRole(user.user_id || user.id, roleData));
        }

        Promise.all(promises)
            .then(() => {
                if (refreshTable) refreshTable();
                setIsLoading(false);
                if (hasEmailChanged) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'info',
                        icon: <InfoSVG />,
                        backgroundColor: palette.sapphire[900],
                        centered: true,
                        title: t('users.table.menu.edit.alerts.emailSuccess.title', { email }),
                        description: t('users.table.menu.edit.alerts.emailSuccess.description')
                    }));
                    onHide();
                    setOpenAlert(true);
                }
                else {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'success',
                        description: t('users.table.menu.edit.alerts.roleSuccess', { fullname }),
                        className: 'clientsMC_Alert--success'
                    }));
                    onHide();
                    setOpenAlert(true);
                }
            })
            .catch(e => {
                console.error(e);
                setIsLoading(false);
                const errorData = e.response?.data?.error;

                if (hasEmailChanged && errorData === EMAIL_IN_USE_ERROR) {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'warning',
                        icon: <InfoSVG />,
                        backgroundColor: palette.yellow[400],
                        centered: true,
                        title: t('users.table.menu.edit.alerts.emailError.title', { email }),
                        description: t('users.table.menu.edit.alerts.emailError.description')
                    }));
                    onHide();
                    setOpenAlert(true);
                }
                else {
                    setAlert((prevAlert) => ({
                        ...prevAlert,
                        severity: 'error',
                        title: t('errors.generalTitle'),
                        description: t('errors.generalDescription'),
                        className: 'clientsMC_Alert--error'
                    }));
                    setOpenAlert(true);
                }
            });
    };

    const onFocus = (key) => {
        if (error[key]) {
            return setError({ ...error, [key]: '' });
        }
    };

    const validate = () => {
        const currentError = { ...error };
        let hasError = false;

        // Check for valid email
        if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
            currentError.email = t('users.table.menu.edit.inputs.email.error');
            hasError = true;
        }
        if (!role) {
            currentError.role = t('users.table.menu.edit.inputs.role.error');
            hasError = true;
        }
        if (hasError) {
            setError(currentError);
            return false;
        }
        return true;
    };

    return (
        <Dialog open={show} onClose={() => onHide()} PaperProps={{ style: { minWidth: '32.308rem', borderRadius: '0.615rem', padding: '2rem' } }}>
            <>
                <Box display='flex' gap={'0.615rem'} alignItems='center'>
                    <EditUserSVG />
                    <span className='delete_text-title' style={{ marginBottom: 0 }}>{t('users.table.menu.edit.title')}</span>
                </Box>
                <Stack direction={'row'} gap={'13rem'} marginTop={'1.5rem'} marginBottom={'0.25rem'}>
                    <Stack direction={'column'} gap={'0.154rem'}>
                        <Typography>{t('users.table.menu.add.inputs.documentType.label')}</Typography>
                        <Typography variant='body1'><b>{documentType}</b></Typography>
                    </Stack>
                    <Stack direction={'column'} gap={'0.154rem'} alignItems={'flex-start'}>
                        <Typography>{t('users.table.columns.document')}</Typography>
                        <Typography variant='body1'><b>{document}</b></Typography>
                    </Stack>
                </Stack>
                <Divider sx={{ borderColor: palette.success[500], borderBottomWidth: 1, opacity: 1, mt: 0, mb: 1 }} />

                <Stack direction={'column'} gap={'0.154rem'} marginTop={'0.5rem'} marginBottom={'0.25rem'}>
                    <Typography variant='body1'>{t('users.table.menu.edit.inputs.fullname.label')}</Typography>
                    <Typography variant='body1'><b>{fullname}</b></Typography>
                </Stack>
                <Divider sx={{ borderColor: palette.success[500], borderBottomWidth: 1, opacity: 1, mt: 0, mb: 1 }} />

                <Stack direction={'column'} gap={'2rem'} marginTop={'2rem'}>

                    <Stack direction={'column'} gap={'0.308rem'}>
                        <Typography variant='body1'>{t('users.table.menu.edit.inputs.email.label')}</Typography>
                        <TextField
                            placeholder={t('users.table.menu.edit.inputs.email.placeholder')}
                            error={!!error.email}
                            fullWidth
                            autoComplete="off"
                            variant='outlined'
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            onFocus={() => onFocus('email')}
                            helperText={error.email ? error.email : ''}
                            size='small'
                            InputProps={{
                                startAdornment: (
                                    <Box display="flex" alignItems="center" mr={1}>
                                        <EmailSVG customStroke={palette.success[500]} />
                                    </Box>
                                )
                            }}
                        />
                        <Stack />
                        <Typography variant='body1' sx={{ mt: 1 }}>{t('users.table.menu.edit.inputs.role.label')}</Typography>
                        <FormControl fullWidth size="small" error={!!error.role}>
                            <Select
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                onFocus={() => onFocus('role')}
                                renderValue={(selected) => {
                                    return roleLabels[selected] || selected;
                                }}
                            >
                                <MenuItem value={ROLES.admin}>{t('users.table.status.admin.natura')}</MenuItem>
                                <MenuItem value={ROLES.operator}>{t('users.table.status.operator.natura')}</MenuItem>
                                <MenuItem value={ROLES.userAdmin}>{t('users.table.status.user_admin.natura')}</MenuItem>
                                <MenuItem value={ROLES.externalSupport}>{t('users.table.status.ext-support-operator.natura')}</MenuItem>
                                <MenuItem value={ROLES.fraudPrevention}>{t('users.table.status.fraud_prevention_analyst.natura')}</MenuItem>
                                <MenuItem value={ROLES.fraudPreventionAdmin}>{t('users.table.status.fraud_prevention_admin.natura')}</MenuItem>
                            </Select>
                            {error.role && <FormHelperText sx={{ color: palette.error.main, marginLeft: '0.75rem' }}>{error.role}</FormHelperText>}
                        </FormControl>
                    </Stack>
                </Stack>
                <Box
                    display='flex'
                    justifyContent='right'
                    marginTop={'1rem'}
                    gap='2.461rem'>
                    <Button className='delete_button--cancel' onClick={onHide} disabled={isLoading}>
                        {t('buttons.cancel')}
                    </Button>
                    <Button className="delete_button--submit" appearance="primary" onClick={editUser} disabled={isLoading || !hasChanges}>
                        {(isLoading ? <CircularProgress size={24} style={{ color: palette.white }} />
                            : t('buttons.save'))}
                    </Button>
                </Box>
            </>
        </Dialog>
    );
};
